// articles/HowToChoosePairings.js
// Second published guide. Target keyword: "how to choose golf pairings" /
// "balanced flights vs random pairings". Decision-guide format: the reader
// self-identifies their goal, then follows the path for that goal.
// Keyword in H1 and first 100 words; multiple internal links.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, InlineLink, Callout, GuideCTA, FAQ } from '../articleUI';

const guide = getGuideBySlug('how-to-choose-golf-pairings');

export default function HowToChoosePairings() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Choose Golf Pairings: Random, Balanced, or Fixed
      </h1>
      <p style={{ fontSize: 15, color: colors.gray400, margin: '0 0 28px' }}>
        {guide.readMinutes} min read
      </p>

      {/* Keyword in first 100 words */}
      <P>
        When you organize a group, choosing golf pairings feels like it should have one right
        answer. It doesn't. The choice between random pairings and balanced flights isn't about
        which is better in the abstract — it's entirely about what you're trying to accomplish. A
        weekend group that plays for fun wants something completely different from a league running
        a season-long points race. Get the goal right and the method picks itself. This guide walks
        through the three situations organizers actually face, so you can match the pairing method
        to the outcome you want instead of guessing.
      </P>

      <Callout>
        Short version: playing for fun and connection → <strong>random</strong>. Running a fair
        competition → <strong>balanced by handicap</strong>. Friends who just want to play together
        → <strong>fixed/manual</strong>. The rest of this guide explains why, and how to set each
        up.
      </Callout>

      <H2>Situation 1: The social group — random pairings</H2>
      <P>
        If you run a weekend group, a buddies' outing, or any group where the point is the company
        and the round, your goal isn't competitive fairness — it's variety. You want people mixing,
        getting to know players they don't usually golf with, and avoiding the same four guys
        riding together every single week. Cliques form fast in a group that always pairs the same
        way, and nothing kills a casual group's energy quicker.
      </P>
      <P>
        For this, <strong>random pairings</strong> are not just acceptable — they're the right
        call. Shuffle the field, accept the result, move on. There's no handicap math to do and no
        fairness to engineer, because nobody's keeping a season standings sheet. The only thing you
        want is a different mix than last week.
      </P>
      <H3>How much tool do you need for this?</H3>
      <P>
        Almost none. A randomize button saves you from drawing names out of a hat, but honestly, a
        social group can run on very little. If you're already using software to handle{' '}
        <InlineLink to="/guides/how-to-organize-a-weekly-golf-group">
          RSVPs and reminders
        </InlineLink>
        , one-tap randomized pairings are a nice convenience — but this is the situation where the
        software matters least. We'll be straight with you: if random pairings are all you need, you
        don't need much.
      </P>

      <H2>Situation 2: The competitive league — balanced groups by handicap</H2>
      <P>
        Now the goal flips. If your group runs any kind of competition — a weekly skins game, a
        season points race, team events, a club championship feeding into standings — fairness is
        the entire point. And random pairings actively work against fairness, because random
        sometimes drops your three lowest-handicap players into the same group and lets them run the
        table week after week. Members notice. Resentment builds. The competition stops feeling
        legitimate.
      </P>
      <P>
        The fix is <strong>balanced flights</strong>: distributing skill evenly so every group has a
        comparable mix of strong and weaker players. The standard method is a snake (or serpentine)
        draft by handicap. Here's how it works in practice:
      </P>
      <UL>
        <LI>
          Rank every player by <strong>course handicap</strong> — the adjusted number based on the
          tees they're playing, not just their raw index.
        </LI>
        <LI>
          Split the ranked field into tiers. For foursomes that's four tiers (A = lowest handicaps,
          down to D = highest); for threesomes it's three (A/B/C).
        </LI>
        <LI>
          Build each group by taking one player from each tier — so every foursome has an A, a B, a
          C, and a D. That's what makes the groups comparable.
        </LI>
      </UL>
      <P>
        Done by hand, this is genuinely tedious. You're sorting the whole field, splitting it into
        tiers, and dealing one player from each tier into each group — and you're redoing it every
        week because handicaps shift and the roster changes. Five minutes of careful pairing math
        per week is a few hours across a season, and a single mistake means an unfair group and an
        earful at the first tee.
      </P>
      <P>
        This is the situation where software earns its keep. Tee Time Organizer builds balanced
        flights by snake-draft instantly — and not just for foursomes. It balances{' '}
        <strong>twosomes, threesomes, and foursomes</strong> with one click, tiering by course
        handicap and labeling each player's tier so you can see the balance at a glance. What took
        you ten minutes of sorting takes a moment, and it's precise every time, even as handicaps
        change week to week. For a competitive group, that's not a convenience — it's the
        difference between a league that feels fair and one that quietly loses members.
      </P>

      <Callout>
        Why <strong>course</strong> handicap and not raw index? Because fairness depends on the tees
        each player actually plays. Balancing on raw index ignores that a player on the forward tees
        gets a different course handicap than the same index from the tips. Balanced flights are
        only truly balanced when they're built on course handicaps — which is why pulling{' '}
        <InlineLink to="/">
          GHIN indexes and computing course handicaps automatically
        </InlineLink>{' '}
        matters for competitive groups.
      </Callout>

      <H2>Situation 3: The fixed group — manual pairings</H2>
      <P>
        There's a third case that's neither random nor balanced: groups of friends who simply want
        to play together, every time. A regular foursome that's been golfing together for years
        doesn't want to be shuffled or balanced — they want their group. For this, the right answer
        is <strong>manual pairings</strong>: set the groups the way the players want them and save
        that arrangement. No algorithm needed, because the goal isn't variety or fairness — it's
        keeping a standing arrangement intact.
      </P>
      <P>
        Most groups are actually a blend. You might have a few fixed foursomes who always play
        together and a pool of floaters you pair around them. Good pairing tools let you lock the
        fixed groups and randomize or balance only the rest — which is why manual drag-and-drop
        matters even when you mostly rely on automatic methods.
      </P>

      <H2>A quick decision guide</H2>
      <P>So, to put it all together — ask yourself what the round is <em>for</em>:</P>
      <UL>
        <LI>
          <strong>Fun, mixing, connection</strong> (weekend warriors, buddies, social leagues) →
          random pairings. Minimal tooling needed.
        </LI>
        <LI>
          <strong>Fair competition</strong> (skins, points races, team events, standings) → balanced
          flights by course handicap. This is where software saves real time.
        </LI>
        <LI>
          <strong>Keeping set groups together</strong> (established foursomes) → manual/saved
          pairings.
        </LI>
        <LI>
          <strong>A mix</strong> (most real groups) → lock the fixed groups, balance or randomize
          the rest.
        </LI>
      </UL>
      <P>
        The mistake isn't choosing random over balanced or vice versa — it's choosing a method that
        doesn't match what your group is actually trying to do. A social group forced into rigid
        balanced flights feels needlessly serious; a competitive league running on random pairings
        feels unfair. Start with the goal, and the method follows.
      </P>

      <H2>However you pair, the output is the tee sheet</H2>
      <P>
        Whichever method you land on, the result is the same artifact: the{' '}
        <strong>tee sheet</strong> — the list of who's playing in which group, in what order, at
        what time. It's what your players check to find their group, and it's what you hand the pro
        shop so the starter knows who's going off and when. A good tee sheet ends first-tee
        confusion before it starts.
      </P>
      <P>
        The practical advantage of building pairings in software rather than on paper is that the
        tee sheet comes out the other end automatically. Once you've randomized, balanced, or
        arranged your groups, Tee Time Organizer produces a clean tee sheet you can print or email
        to every player and to the pro shop in one step — so the pairing decision and the
        distribution that follows it aren't two separate chores.
      </P>

      <FAQ
        id="choose-pairings"
        faqs={[
          {
            q: 'Should I use random or balanced pairings for my golf group?',
            a: 'It depends on the goal. If the round is about fun and mixing players, random pairings are the right call. If there is a competition where fairness matters, balanced flights by course handicap keep groups comparable so the same strong players do not win every week. Groups of friends who want to play together are best served by fixed, manual pairings.',
          },
          {
            q: 'What are balanced flights in golf?',
            a: 'Balanced flights distribute skill evenly across groups. Players are ranked by course handicap and split into tiers (A through D for foursomes, A through C for threesomes), and each group takes one player from each tier — usually via a snake draft. The result is groups of comparable overall strength, which is what makes a competition fair.',
          },
          {
            q: 'Why use course handicap instead of handicap index for pairings?',
            a: 'Because fairness depends on the tees each player actually plays. The same handicap index produces a different course handicap from the forward tees than from the tips, based on course rating, slope, and par. Balancing on raw index ignores that, so truly balanced flights are built on course handicaps.',
          },
          {
            q: 'What is a tee sheet?',
            a: 'The tee sheet is the finished list of who is playing in which group, in what order, at what time. Players check it to find their group, and it is what you hand the pro shop so the starter knows who is going off and when. Building pairings in software produces the tee sheet automatically, ready to print or email.',
          },
        ]}
      />

      <GuideCTA />
    </article>
  );
}
